<script setup lang="ts">
import { computed, ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { UiIcon, UiTabs, UiTabsItem, UiText } from "@shopexperts/shared";

import BonusSignalPresetsPanel from "@/app/components/confidence-signals/BonusSignalPresetsPanel.vue";
import ConfidenceSignalDefinitionsPanel from "@/app/components/confidence-signals/ConfidenceSignalDefinitionsPanel.vue";
import {
  listBonusSignalPresets,
  listConfidenceSignalGroups,
} from "@/app/services/confidenceSignalsApi.service";

const activeTab = ref("definitions");

const { data: groupsResult } = useQuery({
  queryKey: ["admin", "confidence-signal-groups"],
  queryFn: listConfidenceSignalGroups,
  staleTime: 60_000,
});

const { data: presets } = useQuery({
  queryKey: ["admin", "bonus-signal-presets"],
  queryFn: () => listBonusSignalPresets(),
  staleTime: 30_000,
});

const definitions = computed(() =>
  (groupsResult.value?.groups ?? []).flatMap((group) => group.signals ?? []),
);

const summary = computed(() => ({
  groups: groupsResult.value?.groups.length ?? 0,
  definitions: definitions.value.length,
  enabled: definitions.value.filter((signal) => signal.status === "enabled").length,
  // Straight from the API: scoped definitions mean the agency and freelancer
  // ceilings differ, and summing every definition would count both variants of
  // the same signal and produce a total no expert can reach.
  availablePoints: groupsResult.value?.totalAvailablePoints ?? { agency: 0, freelancer: 0 },
}));

const presetCount = computed(() => presets.value?.length ?? 0);
</script>

<template>
  <main class="flex-1 overflow-y-auto bg-secondary p-6 font-light lg:p-8">
    <div class="mx-auto flex w-full max-w-[90rem] flex-col gap-5">
      <header class="max-w-3xl">
        <div class="mb-2 flex items-center gap-2 text-emerald-700">
          <UiIcon name="shield-tick" class="h-5 w-5" />
          <UiText as="span" variant="caption" class="font-semibold uppercase tracking-[0.14em]">
            Trust system
          </UiText>
        </div>
        <h1 class="text-3xl font-normal text-gray-950">Trust Signals</h1>
        <UiText as="p" variant="p" class="mt-2 max-w-2xl text-se-gray-600">
          Definitions set what every expert can earn. Bonus signals credit individual experts for
          things the definition list does not cover, without raising anyone's ceiling.
        </UiText>
      </header>

      <section class="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white sm:flex-row sm:items-stretch">
        <div class="flex flex-1 items-center gap-3 px-4 py-3">
          <span class="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-50 text-emerald-700">
            <UiIcon name="shield-tick" class="h-5 w-5" />
          </span>
          <div>
            <p class="text-xs font-semibold uppercase tracking-[0.12em] text-gray-400">Registry status</p>
            <p class="mt-0.5 text-sm text-gray-700">
              <strong class="font-semibold text-gray-950">{{ summary.definitions }}</strong> definitions across
              <strong class="font-semibold text-gray-950">{{ summary.groups }}</strong> groups,
              <strong class="font-semibold text-gray-950">{{ presetCount }}</strong> bonus presets
            </p>
          </div>
        </div>
        <div class="grid grid-cols-3 border-t border-gray-200 sm:border-l sm:border-t-0">
          <div class="min-w-28 px-5 py-3">
            <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Enabled</p>
            <p class="mt-0.5 text-xl font-semibold tabular-nums text-emerald-700">{{ summary.enabled }}</p>
          </div>
          <div class="min-w-32 border-l border-gray-200 px-5 py-3">
            <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Agency ceiling</p>
            <p class="mt-0.5 text-xl font-semibold tabular-nums text-gray-950">{{ summary.availablePoints.agency }} pts</p>
          </div>
          <div class="min-w-32 bg-gray-950 px-5 py-3 text-white">
            <p class="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Freelancer ceiling</p>
            <p class="mt-0.5 text-xl font-semibold tabular-nums">{{ summary.availablePoints.freelancer }} pts</p>
          </div>
        </div>
      </section>

      <UiTabs v-model="activeTab" default-value="definitions">
        <UiTabsItem value="definitions" label="Definitions" :count="summary.definitions">
          <div class="pt-5">
            <ConfidenceSignalDefinitionsPanel />
          </div>
        </UiTabsItem>

        <UiTabsItem value="bonus_presets" label="Bonus signals" :count="presetCount">
          <div class="pt-5">
            <BonusSignalPresetsPanel />
          </div>
        </UiTabsItem>
      </UiTabs>
    </div>
  </main>
</template>
